import { ExternalLink } from 'lucide-react';
import { PortalDsBadge, PortalDsButton, portalDs } from 'src/components/portal-design-system';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/components/ui/tooltip';
import { cn } from 'src/lib/utils';
import type { AttachedMarket } from 'src/redux/survey/prediction-markets.endpoints';
import {
  formatCloseDate,
  formatProbability,
  formatUpdatedAt,
  providerLabel,
  topOutcomes,
} from './market-utils';

interface MarketDisplayCardProps {
  attachment: AttachedMarket;
  onRemove: () => void;
  onMoveEarlier?: () => void;
  onMoveLater?: () => void;
  canMoveEarlier?: boolean;
  canMoveLater?: boolean;
}

export function MarketDisplayCard({
  attachment,
  onRemove,
  onMoveEarlier,
  onMoveLater,
  canMoveEarlier = false,
  canMoveLater = false,
}: MarketDisplayCardProps) {
  const market = attachment.market;
  const outcomes = topOutcomes(market);
  const showSingleProbability = outcomes.length <= 1;
  const primaryProbability = market.primary_probability ?? outcomes[0]?.probability;

  return (
    <div className={cn(portalDs.card, portalDs.cardPadding, 'flex min-w-[280px] flex-1 flex-col')}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <PortalDsBadge variant="gray">{providerLabel(market.provider)}</PortalDsBadge>
            {attachment.stale && (
              <PortalDsBadge variant="amber">Stale</PortalDsBadge>
            )}
            {market.thin_market && (
              <PortalDsBadge variant="gray">Thin market</PortalDsBadge>
            )}
          </div>
          <h3 className={portalDs.headingH3}>{market.title}</h3>
          {market.subtitle && <p className={cn(portalDs.bodySm, 'mt-1')}>{market.subtitle}</p>}
        </div>
        <a
          href={market.url}
          target="_blank"
          rel="noreferrer"
          className={portalDs.link}
        >
          View
          <ExternalLink className="ml-1 inline size-3.5" />
        </a>
      </div>

      <div className="mb-4">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className={cn(portalDs.caption, 'underline decoration-dotted underline-offset-4')}>
                Market-implied probability
              </p>
            </TooltipTrigger>
            <TooltipContent>
              These are traded prices that include spread and fees, not calibrated forecasts.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {showSingleProbability ? (
          <p className="mt-2 text-[26px] font-bold tracking-[-0.8px] text-[#0D0F1A]">
            {formatProbability(primaryProbability)}
          </p>
        ) : (
          <div className="mt-3 space-y-2">
            {outcomes.map((outcome) => (
              <div key={outcome.label} className="flex items-center justify-between text-[13.5px]">
                <span className="text-[#5E6278]">{outcome.label}</span>
                <span className="font-semibold text-[#0D0F1A]">{formatProbability(outcome.probability)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={cn(portalDs.bodySm, 'mt-auto space-y-1')}>
        <p>Closes {formatCloseDate(market.close_time)}</p>
        <p>
          Last updated {attachment.stale
            ? formatUpdatedAt(attachment.snapshot_timestamp)
            : formatUpdatedAt(market.fetched_at)}
        </p>
        {attachment.relevance_note && (
          <p className="pt-2 text-[#0D0F1A]">{attachment.relevance_note}</p>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {canMoveEarlier && (
          <PortalDsButton
            type="button"
            variant="secondary"
            size="sm"
            onClick={onMoveEarlier}
          >
            Move left
          </PortalDsButton>
        )}
        {canMoveLater && (
          <PortalDsButton
            type="button"
            variant="secondary"
            size="sm"
            onClick={onMoveLater}
          >
            Move right
          </PortalDsButton>
        )}
        <PortalDsButton
          type="button"
          variant="dangerGhost"
          size="sm"
          onClick={onRemove}
        >
          Remove
        </PortalDsButton>
      </div>
    </div>
  );
}
